import posthog from 'posthog-js'
import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { trackGooglePageView } from './google-ads'
import { trackMetaPageView } from './meta-pixel'

// Web analytics event names. This is a deliberate, local mirror of the `web_*`
// block in packages/shared/src/analytics/events.ts (the cross-surface source of
// truth). The marketing bundle does NOT import @verso/shared on purpose: that
// barrel pulls zod + the full constant corpus and would bloat a lightweight
// landing page. The strings below are the contract and must stay identical to
// events.ts. The static deep-link pages (public/analytics.js) mirror them too.
export const WEB_EVENTS = {
  WEB_DOWNLOAD_CTA_CLICKED: 'web_download_cta_clicked',
  WEB_INSTAGRAM_CTA_CLICKED: 'web_instagram_cta_clicked',
  WEB_WAITLIST_SUBMITTED: 'web_waitlist_submitted',
  WEB_WAITLIST_FAILED: 'web_waitlist_failed',
  WEB_DESTINATION_FLIPBOOK_OPENED: 'web_destination_flipbook_opened',
  WEB_DESTINATION_PLATE_ADVANCED: 'web_destination_plate_advanced',
  WEB_DESTINATION_BRANCH_PICKED: 'web_destination_branch_picked',
  WEB_LANDING_SECTION_VIEWED: 'web_landing_section_viewed',
  // The three app-banner events are fired as LITERALS by public/app-banner.js
  // (copied verbatim by Vite, so it cannot import this module). They are
  // declared here because this file is the contract for the `web_*` namespace -
  // keep the strings byte-identical on both sides.
  WEB_APP_BANNER_SHOWN: 'web_app_banner_shown',
  WEB_APP_BANNER_CTA_CLICKED: 'web_app_banner_cta_clicked',
  WEB_APP_BANNER_DISMISSED: 'web_app_banner_dismissed',
  WEB_STICKY_CTA_SHOWN: 'web_sticky_cta_shown',
} as const

// PostHog project key is a PUBLIC client key (ingest-only, safe to ship) - same
// key baked into the mobile bundle and the static deep-link pages. Env wins so a
// different project can be targeted per build; the fallback guarantees the
// deployed site always reports without extra env wiring.
const FALLBACK_KEY = 'phc_tbEDu7ZauyS9qFFwgsneAqgJTrLJC4NrG5NZRqbSBQSS'

// Last-known feature flags, cached so a REPEAT visitor resolves the landing hero
// variant at 0ms (via posthog's `bootstrap`) instead of waiting on the /decide
// round trip.
//
// The invariant is: the cache mirrors the LAST SUCCESSFUL flag response, and
// nothing else. A successful response with flags overwrites it; a successful
// response with NO flags (every flag deleted or the visitor rolled out of them)
// DELETES it; a FAILED response leaves it alone. Only the failure case is
// "self-correcting by the next load" - the empty case is not, because
// experiments.ts settles synchronously off the bootstrap and unsubscribes, so a
// retained cache would bootstrap a dead variant forever. See onFeatureFlags.
// All storage access is best-effort - a Safari private-mode quota error must not
// cost us analytics, let alone the page.
const FLAG_CACHE_KEY = 'compass_ph_bootstrap_v1'

interface FlagCache {
  distinctID: string
  featureFlags: Record<string, string | boolean>
}

function readFlagCache(): FlagCache | undefined {
  try {
    const raw = window.localStorage.getItem(FLAG_CACHE_KEY)
    if (!raw) return undefined
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return undefined
    const { distinctID, featureFlags } = parsed as Partial<FlagCache>
    if (typeof distinctID !== 'string' || !distinctID) return undefined
    if (!featureFlags || typeof featureFlags !== 'object') return undefined
    return { distinctID, featureFlags }
  } catch {
    return undefined
  }
}

function writeFlagCache(featureFlags: Record<string, string | boolean>): void {
  try {
    const distinctID = posthog.get_distinct_id()
    if (!distinctID) return
    window.localStorage.setItem(FLAG_CACHE_KEY, JSON.stringify({ distinctID, featureFlags }))
  } catch {
    // swallow - see readFlagCache
  }
}

function clearFlagCache(): void {
  try {
    window.localStorage.removeItem(FLAG_CACHE_KEY)
  } catch {
    // swallow - see readFlagCache
  }
}

let initialized = false

export function initWebAnalytics(): void {
  if (initialized) return
  const key = import.meta.env.VITE_POSTHOG_API_KEY || FALLBACK_KEY
  if (!key) return
  const host = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com'
  const bootstrap = readFlagCache()
  try {
    posthog.init(key, {
      api_host: host,
      // Autocapture incidental clicks/inputs + manual named events below.
      autocapture: true,
      // SPA route changes are fired manually via usePageviewTracking - the
      // initial-load auto pageview would otherwise be the only one captured.
      capture_pageview: false,
      capture_pageleave: true,
      // No session recordings (privacy posture; keeps the current policy valid).
      disable_session_recording: true,
      ...(bootstrap ? { bootstrap } : {}),
    })
    initialized = true
    // Guarded exactly like the subscription in lib/experiments.ts: a FAILED flag
    // load fires this callback too, with an empty variant map. Caching that would
    // poison the bootstrap - the next pageview would boot with a truthy-but-empty
    // `featureFlags: {}`, posthog would fire onFeatureFlags synchronously with NO
    // error flag, and the hero variant would lock to control (recorded as a clean
    // exposure, with no hero_variant_fallback marker) before the real /flags
    // response landed. An empty map is never worth caching, error or not.
    //
    // A LIVE empty map is a different case and must not be a bare return: it is
    // the true current state (every flag deleted, or this visitor rolled out of
    // all of them). Keeping the old cache there would bootstrap a variant that no
    // longer exists, and because experiments.ts settles synchronously off the
    // bootstrap and unsubscribes, the live answer would never get a chance to
    // correct it - the stale variant would stick forever. So: drop the cache.
    posthog.onFeatureFlags((_flags, variants, context) => {
      if (context?.errorsLoading) return
      if (!variants || Object.keys(variants).length === 0) {
        clearFlagCache()
        return
      }
      writeFlagCache(variants)
    })
    // A Meta click-id on the landing URL means this session came from a Meta ad.
    // SESSION-scoped on purpose: `register` would persist to localStorage forever,
    // so a single ad click would permanently brand that browser as paid traffic
    // and destroy the paid/organic split it exists to enable.
    if (typeof window !== 'undefined' && window.location.search.includes('fbclid')) {
      posthog.register_for_session({ fbclid_present: true })
    }
  } catch {
    // Analytics must never break the page.
    initialized = false
  }
}

type WebProps = Record<string, string | number | boolean | null | undefined>

export function trackWeb(event: string, props?: WebProps): void {
  if (!initialized) return
  try {
    posthog.capture(event, props)
  } catch {
    // swallow - analytics must never break the page
  }
}

// Both index.html snippets already fire the INITIAL ad-side pageview - Meta's
// `PageView` and the Google tag's own config call - so the hook below skips the
// first route it sees. Keyed on the path rather than a bare boolean so a
// repeated effect run for the same URL (React StrictMode's double-invoke in dev)
// cannot manufacture a phantom second pageview. One variable for both sinks:
// they skip the same first route and fire on the same transitions, and two
// counters that must agree are a bug waiting to happen.
let lastAdPageViewPath: string | null = null

/** Fire a $pageview on every client-side route change. Mount once, in <App>. */
export function usePageviewTracking(): void {
  const location = useLocation()
  useEffect(() => {
    if (initialized) {
      try {
        posthog.capture('$pageview')
      } catch {
        // swallow
      }
    }
    const path = `${location.pathname}${location.search}`
    if (lastAdPageViewPath === null) {
      lastAdPageViewPath = path
    } else if (lastAdPageViewPath !== path) {
      lastAdPageViewPath = path
      trackMetaPageView()
      trackGooglePageView()
    }
  }, [location.pathname, location.search])
}
